import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/auth-context';

interface SubscriptionInfo {
  hasActiveSubscription: boolean;
  currentPlan?: string;
  customerId?: string;
  isCancelled?: boolean;
  endDate?: number;
  renewalDate?: number;
  quantity?: number;
  unitPrice?: number;
  currency?: string;
  status?: string;
}

interface SubscriptionCache {
  data: SubscriptionInfo | null;
  timestamp: number;
  promise: Promise<SubscriptionInfo> | null;
}

// Global cache for subscription data
let subscriptionCache: SubscriptionCache = {
  data: null,
  timestamp: 0,
  promise: null
};

const SUBSCRIPTION_CACHE_DURATION = 30000; // 30 seconds
const REFRESH_INTERVAL = 60000; // 1 minute for background refresh

export const useSubscription = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({
    hasActiveSubscription: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, isLoading: authLoading } = useAuth();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);

  // Enhanced subscription check with detailed information
  const getSubscriptionInfo = useCallback(async (email: string, forceRefresh = false): Promise<SubscriptionInfo> => {
    const now = Date.now();

    // Return cached data if still valid and not forcing refresh
    if (!forceRefresh && subscriptionCache.data && now - subscriptionCache.timestamp < SUBSCRIPTION_CACHE_DURATION) {
      return subscriptionCache.data;
    }

    // Return existing promise if one is already in flight
    if (subscriptionCache.promise && !forceRefresh) {
      return subscriptionCache.promise;
    }

    // Create new promise
    subscriptionCache.promise = (async () => {
      try {
        const searchResponse = await fetch('http://localhost:3002/search-customer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        });

        if (!searchResponse.ok) {
          return { hasActiveSubscription: false };
        }

        const searchData = await searchResponse.json();

        // Debug: Log the search response
        console.log('Search response:', JSON.stringify(searchData, null, 2));

        if (!searchData.customerId) {
          return { hasActiveSubscription: false };
        }

        // Check if customer has active subscriptions
        const subscriptionResponse = await fetch(`http://localhost:3002/customer/${searchData.customerId}/subscriptions`);

        if (!subscriptionResponse.ok) {
          return { hasActiveSubscription: false };
        }

        const subscriptionData = await subscriptionResponse.json();

        // Debug: Log the raw subscription data to understand the structure
        console.log('Raw subscription data:', JSON.stringify(subscriptionData, null, 2));

        if (subscriptionData.subscriptions && subscriptionData.subscriptions.length > 0) {
          const activeSubscription = subscriptionData.subscriptions[0];

          // Debug: Log the active subscription object keys
          console.log('Active subscription keys:', Object.keys(activeSubscription));
          console.log('Active subscription items:', activeSubscription.items);
          console.log('Active subscription current_period_end:', activeSubscription.current_period_end);
          const isCancelled = activeSubscription.status === 'canceled' || activeSubscription.status === 'cancelled';

          // Enhanced data extraction with better error handling
          const quantity = activeSubscription.items?.data?.[0]?.quantity || 1;

          // Handle different price structures
          let unitPrice = 0;
          let currency = 'eur';

          if (activeSubscription.items?.data?.[0]?.price) {
            const price = activeSubscription.items.data[0].price;
            unitPrice = price.unit_amount || price.amount || 0;
            currency = price.currency || 'eur';
          } else if (activeSubscription.amount) {
            // Fallback to subscription amount if price is not in items
            unitPrice = activeSubscription.amount;
            currency = activeSubscription.currency || 'eur';
          }

          // Handle different date field names
          const endDate = activeSubscription.current_period_end ||
                         activeSubscription.current_period_end_timestamp ||
                         activeSubscription.period_end ||
                         undefined;

          // Handle renewal date (next billing date)
          const renewalDate = activeSubscription.current_period_end ||
                             activeSubscription.current_period_end_timestamp ||
                             activeSubscription.period_end ||
                             activeSubscription.next_billing_date ||
                             activeSubscription.billing_cycle_anchor ||
                             undefined;

          // Determine plan based on price or metadata
          let currentPlan = 'Pro';
          if (activeSubscription.metadata?.plan) {
            currentPlan = activeSubscription.metadata.plan;
          } else if (unitPrice > 0) {
            // Try to determine plan based on price
            const priceInEuros = unitPrice / 100; // Assuming price is in cents
            if (priceInEuros >= 10) {
              currentPlan = 'Pro';
            } else if (priceInEuros >= 5) {
              currentPlan = 'Basic';
            }
          }

          const result: SubscriptionInfo = {
            hasActiveSubscription: true,
            currentPlan: currentPlan,
            customerId: searchData.customerId,
            isCancelled: isCancelled,
            endDate: endDate,
            renewalDate: renewalDate,
            quantity: quantity,
            unitPrice: unitPrice,
            currency: currency,
            status: activeSubscription.status
          };

          // Debug: Log the processed result
          console.log('Processed subscription result:', result);

          subscriptionCache.data = result;
          subscriptionCache.timestamp = now;
          return result;
        }

        return { hasActiveSubscription: false };
      } catch (error) {
        console.error('Error checking subscription:', error);
        return { hasActiveSubscription: false };
      } finally {
        subscriptionCache.promise = null;
      }
    })();

    return subscriptionCache.promise;
  }, []);

  // Manual refresh function
  const refreshSubscription = useCallback(async () => {
    if (!user?.email) return;

    setIsRefreshing(true);
    setError(null);

    try {
      const info = await getSubscriptionInfo(user.email, true);
      setSubscriptionInfo(info);
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setError('Erreur lors de l\'actualisation des données d\'abonnement');
    } finally {
      setIsRefreshing(false);
    }
  }, [user?.email, getSubscriptionInfo]);

  // Check user's subscription status with caching and background refresh
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user?.email || authLoading) return;

      try {
        setIsLoading(true);
        const info = await getSubscriptionInfo(user.email);
        setSubscriptionInfo(info);
      } catch (error) {
        console.error('Error checking subscription:', error);
        setSubscriptionInfo({ hasActiveSubscription: false });
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();

    // Set up background refresh interval
    if (user?.email) {
      refreshIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshSubscription();
        }
      }, REFRESH_INTERVAL);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [user?.email, authLoading, refreshSubscription]);

  // Clear cache when user changes
  useEffect(() => {
    if (user?.email && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    } else if (user?.email) {
      // Clear cache on user change to ensure fresh data
      subscriptionCache.data = null;
      subscriptionCache.timestamp = 0;
    }
  }, [user?.email]);

  // Clear error when subscription info changes
  useEffect(() => {
    if (subscriptionInfo.hasActiveSubscription) {
      setError(null);
    }
  }, [subscriptionInfo]);

  return {
    subscriptionInfo,
    isLoading,
    isRefreshing,
    error,
    refreshSubscription,
    clearError: () => setError(null)
  };
};
